/**
 * Trees — the jungle, and the only solid obstacle in the world.
 *
 * Kate: "you cannot walk through a tree. If you run into a tree, you bump into it and
 * you lose one, one and a half hearts." So trees block movement AND cost 1.5 hearts on
 * the bump. Birds nest in them (M4) and cats climb them (M4), so trunks carry a
 * canopy height as well as a collision radius.
 *
 * Trunks are drawn with InstancedMesh — a dense jungle is several hundred trees and
 * one draw call per species beats several hundred.
 */
import * as THREE from 'three'
import { type Circle, MAP_HALF_EXTENT, WATER } from './map'
import { heightAt } from './terrain'
import { createBarkTexture, createFoliage } from './textures'

export interface Tree extends Circle {
  /** Height of the canopy underside — the ceiling a bird may fly to (M4). */
  canopyHeight: number
}

const TRUNK_RADIUS_MIN = 0.28
const TRUNK_RADIUS_MAX = 0.55
/** Overlapping blobs per crown. One sphere reads as a lollipop; three read as a tree. */
const CANOPY_BLOBS = 3
/** Radial segments per canopy blob. Halved on touch, where the fill cost matters more. */
const CANOPY_SEGMENTS = (typeof window !== 'undefined' &&
  window.matchMedia?.('(pointer: coarse)').matches) ? 10 : 14
const UP = new THREE.Vector3(0, 1, 0)

/** Displace a sphere's vertices so every canopy blob has an irregular outline. */
function roughenSphere(geometry: THREE.SphereGeometry, random: () => number): void {
  const position = geometry.attributes.position as THREE.BufferAttribute
  const vector = new THREE.Vector3()
  for (let i = 0; i < position.count; i++) {
    vector.fromBufferAttribute(position, i)
    // Two scales of displacement: a broad lumpiness, plus a finer break-up that only
    // shows now that the sphere has vertices to carry it.
    const broad = 0.82 + random() * 0.36
    const fine = 1 + (random() - 0.5) * 0.14
    vector.multiplyScalar(broad * fine)
    position.setXYZ(i, vector.x, vector.y, vector.z)
  }
  position.needsUpdate = true
  geometry.computeVertexNormals()
}

/**
 * Uniform grid over the map so collision only tests the handful of trees in the
 * player's own cell and its neighbours, not all several hundred.
 */
export class TreeGrid {
  private readonly cellSize = 6
  private readonly cells = new Map<number, Tree[]>()
  readonly trees: Tree[] = []

  private key(cx: number, cz: number): number {
    // Offset into positive space so the key stays a small non-negative integer.
    return (cx + 512) * 1024 + (cz + 512)
  }

  add(tree: Tree): void {
    this.trees.push(tree)
    const cx = Math.floor(tree.x / this.cellSize)
    const cz = Math.floor(tree.z / this.cellSize)
    const k = this.key(cx, cz)
    const bucket = this.cells.get(k)
    if (bucket) bucket.push(tree)
    else this.cells.set(k, [tree])
  }

  /** Every tree in the 3x3 block of cells around a point. */
  near(x: number, z: number): Tree[] {
    const cx = Math.floor(x / this.cellSize)
    const cz = Math.floor(z / this.cellSize)
    const out: Tree[] = []
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const bucket = this.cells.get(this.key(cx + dx, cz + dz))
        if (bucket) out.push(...bucket)
      }
    }
    return out
  }
}

/**
 * Deterministic PRNG so the same seed always grows the same jungle — a shared map is a
 * hard requirement once multiplayer lands (M6), and it makes bugs reproducible now.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export interface Forest {
  group: THREE.Group
  grid: TreeGrid
}

export interface ForestStyle {
  trunk: number
  canopy: number
  /** Canopy radius multiplier — broad round oaks vs narrower jungle crowns. */
  canopySpread: number
  /** Radius of the pond to keep clear, or 0 when the biome has no pond. */
  waterRadius: number
  /** Trunk height as [shortest, extra range]: oaks are shorter than jungle trees. */
  treeHeight: [min: number, range: number]
}

/** Grow the trees: trunks with canopies, avoiding the water and the spawn clearing. */
export function createForest(count: number, style: ForestStyle, seed = 20260816): Forest {
  const random = mulberry32(seed)
  const grid = new TreeGrid()
  const group = new THREE.Group()

  // Tapered, many-sided trunks with real bark rather than 7-sided posts.
  const trunkGeometry = new THREE.CylinderGeometry(0.72, 1.05, 1, 12)
  const bark = createBarkTexture(style.trunk)
  const trunkMaterial = new THREE.MeshStandardMaterial({
    map: bark.map,
    normalMap: bark.normalMap,
    normalScale: new THREE.Vector2(1.4, 1.4),
    roughness: 0.95,
  })

  // Canopies are lumpy, not spherical: the sphere's vertices are pushed in and out so
  // no two crowns share a silhouette, and the leaf texture breaks up the surface.
  // Dense enough that a crown you walk under is a curved mass rather than a handful of
  // flat facets. The blobs are instanced, so this is one geometry however many trees
  // there are — the low count bought nothing.
  const foliage = createFoliage(style.canopy)
  const canopyGeometry = new THREE.SphereGeometry(1, CANOPY_SEGMENTS, Math.round(CANOPY_SEGMENTS * 0.75))
  roughenSphere(canopyGeometry, mulberry32(seed ^ 0x9e37))
  const canopyMaterial = new THREE.MeshStandardMaterial({
    map: foliage.map,
    normalMap: foliage.normalMap,
    normalScale: new THREE.Vector2(1.6, 1.6),
    roughness: 0.88,
    // Smooth-shaded. Flat shading was what made a crown read as a heap of paper
    // facets: every triangle took one normal, so denser geometry only bought more
    // facets. Smooth normals plus leaf-scale texture give a mass with leaves on it.
  })

  // Capacity of at least 1: the ocean biome grows no trees, and a zero-length
  // instance buffer is a needless edge case to carry.
  const capacity = Math.max(count, 1)
  const trunks = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, capacity)
  // Three overlapping blobs per tree so a crown has a broken, layered outline.
  const canopies = new THREE.InstancedMesh(canopyGeometry, canopyMaterial, capacity * CANOPY_BLOBS)
  trunks.castShadow = true
  trunks.receiveShadow = true
  // Trunks cast, canopies don't: three overlapping blobs per tree stacked into a
  // near-opaque shadow layer that blacked out the whole forest floor.
  canopies.castShadow = false
  canopies.receiveShadow = true

  const matrix = new THREE.Matrix4()
  const position = new THREE.Vector3()
  const quaternion = new THREE.Quaternion()
  const scale = new THREE.Vector3()

  const edge = MAP_HALF_EXTENT - 2
  let placed = 0
  let attempts = 0

  while (placed < count && attempts < count * 40) {
    attempts++
    const x = (random() * 2 - 1) * edge
    const z = (random() * 2 - 1) * edge

    // Keep the spawn point clear so you don't start wedged against a trunk.
    if (Math.hypot(x, z) < 6) continue
    // No trees in the pond.
    if (style.waterRadius > 0 && Math.hypot(x - WATER.x, z - WATER.z) < style.waterRadius + 1.5) continue

    const radius = TRUNK_RADIUS_MIN + random() * (TRUNK_RADIUS_MAX - TRUNK_RADIUS_MIN)
    // Reject if it would intersect an existing trunk.
    const crowded = grid.near(x, z).some(
      (t) => Math.hypot(t.x - x, t.z - z) < t.radius + radius + 1.2,
    )
    if (crowded) continue

    const height = style.treeHeight[0] + random() * style.treeHeight[1]
    grid.add({ x, z, radius, canopyHeight: height })

    const ground = heightAt(x, z)
    position.set(x, ground + height / 2, z)
    scale.set(radius, height, radius)
    matrix.compose(position, quaternion, scale)
    trunks.setMatrixAt(placed, matrix)

    const canopyRadius = (2.2 + random() * 1.8) * style.canopySpread
    for (let blob = 0; blob < CANOPY_BLOBS; blob++) {
      const spread = blob === 0 ? 0 : canopyRadius * 0.5
      const blobRadius = canopyRadius * (blob === 0 ? 1 : 0.62 + random() * 0.3)
      const angle = random() * Math.PI * 2
      position.set(
        x + Math.cos(angle) * spread,
        ground + height + canopyRadius * 0.45 + (random() - 0.4) * canopyRadius * 0.5,
        z + Math.sin(angle) * spread,
      )
      quaternion.setFromAxisAngle(UP, random() * Math.PI * 2)
      scale.set(blobRadius, blobRadius * (0.7 + random() * 0.3), blobRadius)
      matrix.compose(position, quaternion, scale)
      canopies.setMatrixAt(placed * CANOPY_BLOBS + blob, matrix)
    }
    quaternion.identity()

    placed++
  }

  trunks.count = placed
  canopies.count = placed * CANOPY_BLOBS
  trunks.instanceMatrix.needsUpdate = true
  canopies.instanceMatrix.needsUpdate = true
  group.add(trunks, canopies)

  return { group, grid }
}
